const PICKER_SELECTOR = '[data-color-property-picker]';
const SWATCH_SELECTOR = '[data-color-property-swatch]';
const EVENT_VARIANT_SELECTED = 'variant:selected';
const EVENT_VARIANT_UPDATE = 'variant:update';

function cssEscape(value) {
  if (window.CSS?.escape) return window.CSS.escape(value);
  return String(value).replace(/"/g, '\\"');
}

function normalize(value) {
  return String(value || '').trim();
}

function productFormSelector(productId) {
  return `product-form-component[data-product-id="${cssEscape(productId)}"]`;
}

function hiddenInput(form, name, token) {
  let input = form.querySelector(`input[name="${cssEscape(name)}"]`);
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    if (token) input.dataset.colorPropertyInput = token;
    form.appendChild(input);
  }
  return input;
}

function variantSizeFromPicker(section, productId) {
  const picker = section.querySelector(`variant-picker[data-product-id="${cssEscape(productId)}"]`);
  if (!picker) return '';

  for (const fieldset of picker.querySelectorAll('fieldset')) {
    const legend = normalize(fieldset.querySelector('legend')?.textContent).toLowerCase();
    if (!legend.includes('size')) continue;
    const checked = fieldset.querySelector('input:checked');
    if (checked) return normalize(checked.value);
  }

  for (const select of picker.querySelectorAll('select')) {
    const label = normalize(section.querySelector(`label[for="${cssEscape(select.id)}"]`)?.textContent).toLowerCase();
    const name = normalize(select.name).toLowerCase();
    if (label.includes('size') || name.includes('size')) return normalize(select.value);
  }

  return '';
}

function variantSizeFromResource(variant) {
  if (!variant) return '';
  if (Array.isArray(variant.options) && variant.options.length) return normalize(variant.options[0]);
  if (variant.option1) return normalize(variant.option1);
  const title = normalize(variant.title);
  if (!title || title.toLowerCase() === 'default title') return '';
  return normalize(title.split('/').pop());
}

function selectedSwatch(picker) {
  return picker.querySelector(`${SWATCH_SELECTOR}.is-selected`) || picker.querySelector(`${SWATCH_SELECTOR}[aria-checked="true"]`) || picker.querySelector(SWATCH_SELECTOR);
}

function colorMediaFor(gallery, color) {
  if (!gallery._bazzaiColorMedia) {
    const node = gallery.querySelector('[data-bazzai-color-media-map]');
    gallery._bazzaiColorMedia = node ? JSON.parse(node.textContent || '{}') : {};
  }
  const target = normalize(color).toLowerCase();
  return Object.values(gallery._bazzaiColorMedia).find((media) => normalize(media?.color).toLowerCase() === target);
}

/**
 * Swaps the curated garment hero to the selected color in-place. The server
 * renders the default-color hero first, so this is pre-paint-safe; later swaps
 * keep the same media box and cannot introduce layout shift. The same role/data
 * contract works for desktop grids, carousels, and mobile slideshows.
 */
function activateColorMedia(section, color) {
  const target = normalize(color).toLowerCase();
  if (!target) return;
  try {
    const gallery = section.querySelector('media-gallery');
    if (!gallery) return;
    const media = colorMediaFor(gallery, color);
    if (!media?.src) return;

    gallery.querySelectorAll('[data-bazzai-media-role="hero"]').forEach((hero) => {
      hero.dataset.mediaColor = normalize(media.color || color);
      const img = hero.querySelector('img');
      if (!img) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = media.src;
      if (media.alt) img.alt = media.alt;
    });
    gallery.dataset.activeColor = normalize(media.color || color);

    const slideshow = gallery.querySelector('slideshow-component');
    if (slideshow && typeof (/** @type {any} */ (slideshow).select) === 'function') {
      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide')).filter(
        (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
      );
      const heroIndex = slides.findIndex((slide) => slide.dataset.bazzaiMediaRole === 'hero');
      if (heroIndex >= 0) /** @type {any} */ (slideshow).select(heroIndex, undefined, { animate: true });
    }
  } catch {
    /* degrade silently */
  }
}

function updateStickyLabel(section, productId, color, size) {
  const sticky = section.querySelector(`sticky-add-to-cart[data-product-id="${cssEscape(productId)}"]`);
  const label = sticky?.querySelector('.sticky-add-to-cart__variant');
  if (!label || !color) return;
  label.hidden = false;
  label.style.display = '';
  label.textContent = `Color: ${color}${size ? ` / Size: ${size}` : ''}`;
}

function initPicker(picker) {
  if (!(picker instanceof HTMLElement) || picker.dataset.colorPropertyReady === 'true') return;
  picker.dataset.colorPropertyReady = 'true';

  const productId = picker.dataset.productId || '';
  const sectionId = picker.dataset.sectionId || '';
  // Inside the quick-add modal the picker's section id points at the (absent)
  // PDP section, and a document-wide lookup can hit the collection card's own
  // quick-add form for the same product. Bind to the modal's form first.
  const modalRoot = picker.closest('#quick-add-modal-content');
  const section = modalRoot || document.getElementById(`shopify-section-${sectionId}`) || picker.closest('.shopify-section') || document;
  const label = picker.querySelector('[data-color-property-selected]');
  const formComponent = section.querySelector(productFormSelector(productId));
  const form = formComponent?.querySelector('form');
  if (!form || !productId) return;

  let selectedColor = normalize(selectedSwatch(picker)?.dataset.colorName);
  let selectedSize = normalize(form.querySelector('[data-color-property-input="size"]')?.value);

  function sync(variant) {
    const color = normalize(selectedColor || selectedSwatch(picker)?.dataset.colorName);
    selectedColor = color;
    selectedSize = normalize(variantSizeFromPicker(section, productId) || variantSizeFromResource(variant) || selectedSize);

    if (color) hiddenInput(form, 'properties[_color]', 'color').value = color;
    // Visible twin: Shopify checkout HIDES underscore-prefixed properties, so
    // buyers could not confirm their color at the final step (QA 2026-07-08).
    if (color) hiddenInput(form, 'properties[Color]', 'color-visible').value = color;
    if (selectedSize) hiddenInput(form, 'properties[_size]', 'size').value = selectedSize;

    const designInput = form.querySelector('[data-color-property-input="design-id"]');
    if (designInput && normalize(designInput.value)) {
      hiddenInput(form, 'properties[_design_id]', 'design-id').value = normalize(designInput.value);
    }

    if (label) label.textContent = color ? `Selected: ${color}` : '';
    updateStickyLabel(section, productId, color, selectedSize);
  }

  function selectSwatch(button) {
    if (!button || !picker.contains(button) || button.disabled) return;
    selectedColor = normalize(button.dataset.colorName);

    picker.querySelectorAll(SWATCH_SELECTOR).forEach((swatch) => {
      const on = swatch === button;
      swatch.classList.toggle('is-selected', on);
      swatch.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    sync();
    activateColorMedia(section, selectedColor);
  }

  // Selection binds on pointerup with a movement tolerance instead of click:
  // scroll flicks and layout shifts between pointerdown and pointerup no
  // longer register as a swatch choice (mobile mis-tap fix).
  const TAP_TOLERANCE_PX = 12;
  let pointerState = null;
  let suppressNextClick = false;

  picker.addEventListener('pointerdown', (event) => {
    const button = event.target.closest(SWATCH_SELECTOR);
    if (!button || !picker.contains(button)) {
      pointerState = null;
      return;
    }
    pointerState = { id: event.pointerId, x: event.clientX, y: event.clientY, button };
  });

  picker.addEventListener('pointerup', (event) => {
    const state = pointerState;
    pointerState = null;
    if (!state || event.pointerId !== state.id) return;

    // The pointer flow owns this interaction — swallow the synthetic click
    // that follows so the tolerance check below cannot be bypassed.
    suppressNextClick = true;
    window.setTimeout(() => {
      suppressNextClick = false;
    }, 400);

    const moved = Math.hypot(event.clientX - state.x, event.clientY - state.y);
    if (moved > TAP_TOLERANCE_PX) return; // treated as a scroll, not a tap
    const upButton = event.target.closest(SWATCH_SELECTOR);
    if (upButton !== state.button) return; // finger drifted onto another swatch
    selectSwatch(state.button);
  });

  picker.addEventListener('pointercancel', () => {
    pointerState = null;
  });

  picker.addEventListener('click', (event) => {
    const button = event.target.closest(SWATCH_SELECTOR);
    if (!button) return;
    // Keyboard / assistive tech clicks (detail === 0) always select.
    if (event.detail === 0) {
      selectSwatch(button);
      return;
    }
    // Pointer-driven clicks were already handled on pointerup.
    if (suppressNextClick) {
      suppressNextClick = false;
      return;
    }
    // Fallback for environments without pointer events.
    selectSwatch(button);
  });

  section.addEventListener('change', (event) => {
    if (event.target?.closest?.(`variant-picker[data-product-id="${cssEscape(productId)}"]`)) {
      window.setTimeout(() => sync(), 0);
    }
  });

  section.addEventListener(EVENT_VARIANT_SELECTED, () => window.setTimeout(() => sync(), 0));
  section.addEventListener(EVENT_VARIANT_UPDATE, (event) => {
    if (event.detail?.data?.productId && event.detail.data.productId !== productId) return;
    sync(event.detail?.resource);
  });
  form.addEventListener('submit', () => sync(), { capture: true });

  sync();
}

function initAll() {
  document.querySelectorAll(PICKER_SELECTOR).forEach(initPicker);
}

initAll();
document.addEventListener('DOMContentLoaded', initAll);
document.addEventListener('shopify:section:load', initAll);
